import { useCreateElplanBidsMutation } from 'api/elplan/elplan.api'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import alertStore from 'store/alert/alert'
import sentry from 'utils/sentry/sentry'
import {
  type BidValue,
  getSpotBidsFromOptElplan,
  splitHourlyBidsInto15MinIntervals,
} from '../../DayAheadBidManagement/DayAheadBidManagement.helper'

type CopySpotBidsFromPlanOptions = {
  datasetStartTime: ISODateTime
  datasetEndTime: ISODateTime
  regularPlanSubtypeAliases?: UiConfigAliases
  scenarioId?: number
  partitionId?: number
  elplanInfoId: number
}

export function useCopySpotBidsFromPlan({
  datasetStartTime,
  datasetEndTime,
  regularPlanSubtypeAliases,
  scenarioId,
  partitionId,
  elplanInfoId,
}: CopySpotBidsFromPlanOptions) {
  const { t } = useTranslation()
  const [copying, setCopying] = useState(false)
  const { mutate: createSpotBids } = useCreateElplanBidsMutation()

  async function copySpotBidsFromPlan(): Promise<void> {
    setCopying(true)
    let bids: BidValue[] = []
    try {
      bids = await getSpotBidsFromOptElplan(
        datasetStartTime,
        datasetEndTime,
        undefined,
        regularPlanSubtypeAliases
      ).then(splitHourlyBidsInto15MinIntervals)
    } catch (err: unknown) {
      sentry.captureException(err, {
        level: 'warning',
        tags: {
          sentry_scope: 'day_ahead_bid_editor',
          issue_type: 'copy_spot_bids_from_production_plan_failure',
        },
        extra: { datasetStartTime, datasetEndTime, scenarioId, elplanInfoId },
        fingerprint: ['day_ahead_bid_editor', 'copy_spot_bids_from_production_plan_failure'],
      })
      alertStore.error(t('Could not copy spot bids from production plan (data missing)'))
      setCopying(false)
      return
    }
    createSpotBids(
      {
        elplan_info: elplanInfoId,
        type: 'spotbid_without_price',
        electricity_price_scenario: scenarioId,
        ...(partitionId === undefined ? {} : { partition: partitionId }),
        bids,
      },
      { onSettled: () => setCopying(false) }
    )
  }

  return { copyingSpotBidsFromPlan: copying, copySpotBidsFromPlan }
}
